import { useState, useEffect } from "react";
import MainLayout from "../../components/layouts/MainLayout";
import { annonceApi } from "../../api/annonceApi";
import { categoryApi } from "../../api/categoryApi";
import { DeviseApi } from "../../api/deviseApi";
import { CountryApi } from "../../api/countryApi";
import Swal from "sweetalert2";
import { message } from "antd";
import { useNavigate } from "react-router-dom";
import { Auth } from "../../utils/Auth";
import { PlusOutlined, MinusCircleOutlined, DeleteOutlined } from "@ant-design/icons";

const AddAnnonce = () => {
    const navigate = useNavigate();
    const [formData, setFormData] = useState({
        title: '',
        description: '',
        city: '',
        country: '',
        category_id: '',
        price: '',
        devise_id: '',
        expired_at: '',
        whatsapp: '',
        highlighting: false,
        is_training: false,
    });

    const [categories, setCategories] = useState([]);
    const [devises, setDevises] = useState([]);
    const [countries, setCountries] = useState([]);
    const [countriesLoading, setCountriesLoading] = useState(true);
    const [countriesError, setCountriesError] = useState(false);
    const [loading, setLoading] = useState(false);
    const [quiz, setQuiz] = useState([]);
    const [selectedImages, setSelectedImages] = useState([]);
    const [imagePreviews, setImagePreviews] = useState([]);
    const abortController = new AbortController();

    // Charger les catégories
    const fetchCategories = async () => {
        try {
            const res = await categoryApi.getCategories(abortController.signal);
            if (res && res.data) {
                setCategories(res.data);
            }
        } catch (error) {
            console.error("Erreur lors du chargement des catégories:", error);
        }
    };

    // Charger les pays actifs (la valeur stockée sur l'annonce est le nom du pays)
    const fetchCountries = async () => {
        setCountriesLoading(true);
        setCountriesError(false);
        try {
            const res = await CountryApi.getCountries({ statut: 'ACTIVE', per_page: 300 }, abortController.signal);
            if (res && res.success) {
                setCountries(res.data?.data ?? []);
            } else {
                setCountriesError(true);
            }
        } catch (error) {
            console.error("Erreur lors du chargement des pays:", error);
            setCountriesError(true);
        } finally {
            setCountriesLoading(false);
        }
    };

    // Charger les devises actives
    const fetchDevises = async () => {
        try {
            const res = await DeviseApi.getActiveDevises(abortController.signal);
            if (res && res.success) {
                setDevises(res.data);
                const defaultDevise = res.data.find((d) => d.is_default);
                if (defaultDevise) {
                    setFormData((prev) => ({ ...prev, devise_id: defaultDevise.id }));
                }
            }
        } catch (error) {
            console.error("Erreur lors du chargement des devises:", error);
        }
    };

    useEffect(() => {
        fetchCategories();
        fetchDevises();
        fetchCountries();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData({
            ...formData,
            [name]: type === 'checkbox' ? checked : value,
        });
    };

    // Gestion des images
    const handleImageChange = (e) => {
        const files = Array.from(e.target.files);
        
        if (files.length === 0) return;

        // Ajouter les nouveaux fichiers
        const newFiles = [...selectedImages, ...files];
        setSelectedImages(newFiles);

        // Créer les prévisualisations
        const newPreviews = [];
        files.forEach((file) => {
            const reader = new FileReader();
            reader.onloadend = () => {
                newPreviews.push({
                    file: file,
                    preview: reader.result,
                });
                if (newPreviews.length === files.length) {
                    setImagePreviews([...imagePreviews, ...newPreviews]);
                }
            };
            reader.readAsDataURL(file);
        });
    };

    const removeImage = (index) => {
        const newImages = selectedImages.filter((_, i) => i !== index);
        const newPreviews = imagePreviews.filter((_, i) => i !== index);
        setSelectedImages(newImages);
        setImagePreviews(newPreviews);
    };

    // Gestion du quiz
    const addQuestion = () => {
        setQuiz([
            ...quiz,
            {
                question: '',
                answers: [
                    { libelle: '', is_true: false },
                    { libelle: '', is_true: false },
                ],
            },
        ]);
    };

    const removeQuestion = (questionIndex) => {
        setQuiz(quiz.filter((_, index) => index !== questionIndex));
    };

    const updateQuestion = (questionIndex, value) => {
        const updatedQuiz = [...quiz];
        updatedQuiz[questionIndex].question = value;
        setQuiz(updatedQuiz);
    };

    const addAnswer = (questionIndex) => {
        const updatedQuiz = [...quiz];
        updatedQuiz[questionIndex].answers.push({ libelle: '', is_true: false });
        setQuiz(updatedQuiz);
    };

    const removeAnswer = (questionIndex, answerIndex) => {
        const updatedQuiz = [...quiz];
        updatedQuiz[questionIndex].answers = updatedQuiz[questionIndex].answers.filter(
            (_, index) => index !== answerIndex
        );
        setQuiz(updatedQuiz);
    };

    const updateAnswer = (questionIndex, answerIndex, field, value) => {
        const updatedQuiz = [...quiz];
        if (field === 'is_true') {
            updatedQuiz[questionIndex].answers.forEach((answer, index) => {
                answer.is_true = index === answerIndex;
            });
        } else {
            updatedQuiz[questionIndex].answers[answerIndex][field] = value;
        }
        setQuiz(updatedQuiz);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);

        try {
            // Créer FormData
            const formDataToSend = new FormData();

            // Ajouter les champs du formulaire
            formDataToSend.append('title', formData.title);
            formDataToSend.append('description', formData.description || '');
            formDataToSend.append('city', formData.city || '');
            formDataToSend.append('country', formData.country || '');
            formDataToSend.append('category_id', formData.category_id);
            formDataToSend.append('price', formData.price || '');
            formDataToSend.append('devise_id', formData.devise_id || '');
            formDataToSend.append('expired_at', formData.expired_at || '');
            formDataToSend.append('whatsapp', formData.whatsapp || '');
            formDataToSend.append('highlighting', formData.highlighting ? 1 : 0);
            formDataToSend.append('is_training', formData.is_training ? 1 : 0);

            // Ajouter les images avec le nom exact medias[]
            selectedImages.forEach((file) => {
                formDataToSend.append('medias[]', file);
            });

            // Ajouter le quiz si is_training est coché
            if (formData.is_training && quiz.length > 0) {
                formDataToSend.append('quiz', JSON.stringify(
                    quiz.map((q) => ({
                        question: q.question,
                        answers: q.answers.map((a) => ({
                            libelle: a.libelle,
                            is_true: a.is_true ? 1 : 0,
                        })),
                    }))
                ));
            }

            const res = await annonceApi.storeAnnonce(
                formDataToSend,
                abortController.signal
            );

            if (!res.success) {
                return Swal.fire({
                    title: "Impossible d'enregistrer",
                    text: res.message,
                });
            }

            message.success("Annonce ajoutée avec succès");
            navigate("/annonces");
        } catch (error) {
            console.log(error);
            // return;
            message.error("Une erreur est survenue lors de l'ajout de l'annonce");
            const messages = await error.messages ?? error.message;

            if (messages instanceof Array) {
                return Swal.fire({
                    title:
                        messages.join("\n") !== "Unauthenticated"
                            ? "Erreur détectée"
                            : "Session expirée",
                    text:
                        messages.join("\n") !== "Unauthenticated"
                            ? "Veuillez vous reconnecter."
                            : "Votre session a expirée veuillez vous reconnecter",
                    icon: "error",
                }).then((result) => {
                    Auth.removeSessionToken();
                    return navigate(`/login`);
                });
            }
        } finally {
            setLoading(false);
        }
    };

    return (
        <MainLayout>
            <div className="max-w-4xl mx-auto p-6 bg-white rounded-lg shadow-md">
                <h2 className="text-2xl font-bold mb-4">Ajouter une annonce</h2>

                <form onSubmit={handleSubmit}>
                    {/* Titre */}
                    <div className="mb-4">
                        <label className="block text-sm font-medium text-gray-700">
                            Titre <span className="text-red-600 text-sm italic">(Obligatoire)</span>
                        </label>
                        <input
                            type="text"
                            name="title"
                            value={formData.title}
                            onChange={handleChange}
                            required
                            className="w-full px-4 py-2 border border-gray-300 rounded-md"
                        />
                    </div>

                    {/* Description */}
                    <div className="mb-4">
                        <label className="block text-sm font-medium text-gray-700">
                            Description
                        </label>
                        <textarea
                            name="description"
                            value={formData.description}
                            onChange={handleChange}
                            rows="4"
                            className="w-full px-4 py-2 border border-gray-300 rounded-md"
                        />
                    </div>

                    {/* Catégorie */}
                    <div className="mb-4">
                        <label className="block text-sm font-medium text-gray-700">
                            Catégorie <span className="text-red-600 text-sm italic">(Obligatoire)</span>
                        </label>
                        <select
                            name="category_id"
                            value={formData.category_id}
                            onChange={handleChange}
                            required
                            className="w-full px-4 py-2 border border-gray-300 rounded-md"
                        >
                            <option value="">Sélectionner une catégorie</option>
                            {categories.map((cat) => (
                                <option key={cat.id} value={cat.id}>
                                    {cat.libelle}
                                </option>
                            ))}
                        </select>
                    </div>

                    {/* Pays */}
                    <div className="mb-4">
                        <label className="block text-sm font-medium text-gray-700">
                            Pays <span className="text-red-600 text-sm italic">(Obligatoire)</span>
                        </label>
                        <select
                            name="country"
                            value={formData.country}
                            onChange={handleChange}
                            required
                            disabled={countriesLoading || countriesError}
                            className="w-full px-4 py-2 border border-gray-300 rounded-md"
                        >
                            <option value="">
                                {countriesLoading
                                    ? "Chargement des pays..."
                                    : countriesError
                                        ? "Impossible de charger les pays"
                                        : countries.length === 0
                                            ? "Aucun pays disponible"
                                            : "Sélectionner un pays"}
                            </option>
                            {countries.map((c) => (
                                <option key={c.id} value={c.name}>
                                    {c.name}
                                </option>
                            ))}
                            {/* Pays déjà enregistré mais absent de la liste active (pays désactivé) : on le conserve */}
                            {formData.country && !countries.some((c) => c.name === formData.country) && (
                                <option value={formData.country}>{formData.country}</option>
                            )}
                        </select>
                        {countriesError && (
                            <p className="text-sm text-red-600 mt-1">
                                Échec du chargement des pays.{" "}
                                <button type="button" onClick={fetchCountries} className="underline">
                                    Réessayer
                                </button>
                            </p>
                        )}
                    </div>

                    {/* Ville */}
                    <div className="mb-4">
                        <label className="block text-sm font-medium text-gray-700">Ville</label>
                        <input
                            type="text"
                            name="city"
                            value={formData.city}
                            onChange={handleChange}
                            className="w-full px-4 py-2 border border-gray-300 rounded-md"
                        />
                    </div>

                    {/* Prix */}
                    <div className="mb-4 grid grid-cols-3 gap-4">
                        <div className="col-span-2">
                            <label className="block text-sm font-medium text-gray-700">Prix</label>
                            <input
                                type="number"
                                name="price"
                                value={formData.price}
                                onChange={handleChange}
                                className="w-full px-4 py-2 border border-gray-300 rounded-md"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700">Devise</label>
                            <select
                                name="devise_id"
                                value={formData.devise_id}
                                onChange={handleChange}
                                className="w-full px-4 py-2 border border-gray-300 rounded-md"
                            >
                                {devises.map((devise) => (
                                    <option key={devise.id} value={devise.id}>
                                        {devise.iso_code}
                                    </option>
                                ))}
                            </select>
                        </div>
                    </div>

                    {/* Date d'expiration */}
                    <div className="mb-4">
                        <label className="block text-sm font-medium text-gray-700">
                            Date d'expiration
                        </label>
                        <input
                            type="datetime-local"
                            name="expired_at"
                            value={formData.expired_at}
                            onChange={handleChange}
                            className="w-full px-4 py-2 border border-gray-300 rounded-md"
                        />
                    </div>

                    {/* WhatsApp */}
                    <div className="mb-4">
                        <label className="block text-sm font-medium text-gray-700">
                            Contact WhatsApp
                        </label>
                        <input
                            type="text"
                            name="whatsapp"
                            value={formData.whatsapp}
                            onChange={handleChange}
                            className="w-full px-4 py-2 border border-gray-300 rounded-md"
                        />
                    </div>

                    {/* Upload d'images */}
                    <div className="mb-4">
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Images
                        </label>
                        <input
                            type="file"
                            name="medias[]"
                            multiple
                            accept="image/*"
                            onChange={handleImageChange}
                            className="w-full px-4 py-2 border border-gray-300 rounded-md"
                        />
                        {imagePreviews.length > 0 && (
                            <div className="mt-4 grid grid-cols-4 gap-4">
                                {imagePreviews.map((preview, index) => (
                                    <div key={index} className="relative">
                                        <img
                                            src={preview.preview}
                                            alt={`Preview ${index + 1}`}
                                            className="w-full h-32 object-cover rounded-md border border-gray-300"
                                        />
                                        <button
                                            type="button"
                                            onClick={() => removeImage(index)}
                                            className="absolute top-1 right-1 bg-red-500 text-white rounded-full p-1 hover:bg-red-600"
                                        >
                                            <DeleteOutlined />
                                        </button>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>

                    {/* Mise en avant */}
                    {/* <div className="mb-4">
                        <label className="flex items-center">
                            <input
                                type="checkbox"
                                name="highlighting"
                                checked={formData.highlighting}
                                onChange={handleChange}
                                className="mr-2"
                            />
                            <span className="text-sm font-medium text-gray-700">
                                Mettre en avant
                            </span>
                        </label>
                    </div> */}

                    {/* Is Training */}
                    {/* <div className="mb-4">
                        <label className="flex items-center">
                            <input
                                type="checkbox"
                                name="is_training"
                                checked={formData.is_training}
                                onChange={handleChange}
                                className="mr-2"
                            />
                            <span className="text-sm font-medium text-gray-700">
                                Formation avec quiz
                            </span>
                        </label>
                    </div> */}

                    {/* Formulaire Quiz */}
                    {formData.is_training && (
                        <div className="mb-6 p-4 border border-gray-300 rounded-md bg-gray-50">
                            <div className="flex justify-between items-center mb-4">
                                <h3 className="text-lg font-semibold">Quiz</h3>
                                <button
                                    type="button"
                                    onClick={addQuestion}
                                    className="px-3 py-1 bg-blue-500 text-white rounded hover:bg-blue-600 flex items-center"
                                >
                                    <PlusOutlined className="mr-1" />
                                    Ajouter une question
                                </button>
                            </div>

                            {quiz.map((question, questionIndex) => (
                                <div
                                    key={questionIndex}
                                    className="mb-4 p-4 border border-gray-200 rounded-md bg-white"
                                >
                                    <div className="flex justify-between items-center mb-2">
                                        <label className="block text-sm font-medium text-gray-700">
                                            Question {questionIndex + 1}
                                        </label>
                                        <button
                                            type="button"
                                            onClick={() => removeQuestion(questionIndex)}
                                            className="text-red-600 hover:text-red-800"
                                        >
                                            <MinusCircleOutlined />
                                        </button>
                                    </div>
                                    <input
                                        type="text"
                                        value={question.question}
                                        onChange={(e) =>
                                            updateQuestion(questionIndex, e.target.value)
                                        }
                                        placeholder="Libellé de la question"
                                        className="w-full px-4 py-2 border border-gray-300 rounded-md mb-3"
                                    />

                                    <div className="ml-4">
                                        <div className="flex justify-between items-center mb-2">
                                            <label className="block text-sm font-medium text-gray-700">
                                                Réponses
                                            </label>
                                            <button
                                                type="button"
                                                onClick={() => addAnswer(questionIndex)}
                                                className="px-2 py-1 bg-green-500 text-white text-xs rounded hover:bg-green-600"
                                            >
                                                <PlusOutlined className="mr-1" />
                                                Ajouter une réponse
                                            </button>
                                        </div>

                                        {question.answers.map((answer, answerIndex) => (
                                            <div
                                                key={answerIndex}
                                                className="mb-2 flex items-center gap-2"
                                            >
                                                <input
                                                    type="text"
                                                    value={answer.libelle}
                                                    onChange={(e) =>
                                                        updateAnswer(
                                                            questionIndex,
                                                            answerIndex,
                                                            "libelle",
                                                            e.target.value
                                                        )
                                                    }
                                                    placeholder="Libellé de la réponse"
                                                    className="flex-1 px-4 py-2 border border-gray-300 rounded-md"
                                                />
                                                <label className="flex items-center">
                                                    <input
                                                        type="radio"
                                                        name={`correct-${questionIndex}`}
                                                        checked={answer.is_true}
                                                        onChange={() =>
                                                            updateAnswer(
                                                                questionIndex,
                                                                answerIndex,
                                                                "is_true",
                                                                true
                                                            )
                                                        }
                                                        className="mr-1"
                                                    />
                                                    <span className="text-sm text-gray-700">
                                                        Bonne réponse
                                                    </span>
                                                </label>
                                                {question.answers.length > 2 && (
                                                    <button
                                                        type="button"
                                                        onClick={() =>
                                                            removeAnswer(questionIndex, answerIndex)
                                                        }
                                                        className="text-red-600 hover:text-red-800"
                                                    >
                                                        <MinusCircleOutlined />
                                                    </button>
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            ))}

                            {quiz.length === 0 && (
                                <p className="text-gray-500 text-sm italic">
                                    Aucune question ajoutée. Cliquez sur "Ajouter une question" pour
                                    commencer.
                                </p>
                            )}
                        </div>
                    )}

                    {/* Soumission */}
                    <div className="mt-6">
                        <button
                            type="submit"
                            className="w-full px-4 py-2 bg-africasa-secondary text-white rounded-md hover:bg-africasa-primary"
                            disabled={loading}
                        >
                            {loading ? "Enregistrement..." : "Ajouter l'annonce"}
                        </button>
                    </div>
                </form>
            </div>
        </MainLayout>
    );
};

export default AddAnnonce;


